import { computed, ref } from 'vue';
import { BG_MODES, type BgMode } from '@s-blog/shared-types';

/**
 * 背景偏好 (模块级单例)。两层模型:
 *   localStorage 有值 → 访客覆盖优先; 无值 → 站长默认 (applyOwnerDefault, 来自 SiteConfig); 都没有 → 'shader'。
 * **持久化只在访客显式操作 (setBgMode/toggleAnimation) 时写 localStorage** —— 不在 watch 里每次写,
 * 否则首屏自动落盘会让"没动过设置的访客"永远吃不到站长默认。
 *
 * 主题: shader(熔暮) / shader-dawn(破晓) / shader-night(子夜) / shader-auto(跟随系统) / classic(经典CSS)。
 */
export type { BgMode };
export type EffectiveShaderTheme = 'dusk' | 'dawn' | 'night';

const STORAGE_KEY = 'aurora-bg';
const LAST_SHADER_KEY = 'aurora-bg-last-shader';
const isValid = (v: string | null): v is BgMode =>
  !!v && (BG_MODES as readonly string[]).includes(v);

// 系统外观 (prefers-color-scheme) 响应式跟踪 — shader-auto 模式消费
const systemDark = ref(false);
try {
  const mq = window.matchMedia('(prefers-color-scheme: dark)');
  systemDark.value = mq.matches;
  mq.addEventListener?.('change', (e) => {
    systemDark.value = e.matches;
  });
} catch {
  // SSR / 老浏览器: 保持 false (auto 模式得到破晓)
}

// 初始: 访客存过就用访客的, 否则内置默认 (站长默认稍后由 applyOwnerDefault patch)
const stored: BgMode | null = (() => {
  try {
    const s = localStorage.getItem(STORAGE_KEY);
    return isValid(s) ? s : null;
  } catch {
    return null;
  }
})();
const hadStored = stored !== null;
const bgMode = ref<BgMode>(stored ?? 'shader');

function persist(v: BgMode) {
  try {
    localStorage.setItem(STORAGE_KEY, v);
  } catch {
    // ignore (privacy mode)
  }
}

export function useBgPreference() {
  function setBgMode(v: BgMode) {
    bgMode.value = v;
    persist(v); // 访客显式选择 → 落盘 (从此覆盖站长默认)
  }
  /** 站长默认 (SiteConfig.defaultBgMode): 仅当访客从没手动设过时套用, 不落盘 (站长改了默认还能再生效) */
  function applyOwnerDefault(v: BgMode | null | undefined) {
    if (!hadStored && isValid(v ?? null)) bgMode.value = v as BgMode;
  }
  // 当前生效 shader 主题 (classic 时无意义给 dusk 兜底); auto 跟随系统, 切换时 AuroraShaderBg 内部 lerp
  const effectiveShaderTheme = computed<EffectiveShaderTheme>(() => {
    switch (bgMode.value) {
      case 'shader-dawn':
        return 'dawn';
      case 'shader-night':
        return 'night';
      case 'shader-auto':
        return systemDark.value ? 'night' : 'dawn';
      default:
        return 'dusk';
    }
  });
  /** 首页「关闭/开启动画」快关: shader ↔ classic, 关闭时记住当前 shader 主题, 再开恢复 */
  function toggleAnimation() {
    if (bgMode.value === 'classic') {
      let last: BgMode = 'shader';
      try {
        const s = localStorage.getItem(LAST_SHADER_KEY);
        if (isValid(s) && s !== 'classic') last = s;
      } catch {
        // ignore
      }
      setBgMode(last);
    } else {
      try {
        localStorage.setItem(LAST_SHADER_KEY, bgMode.value);
      } catch {
        // ignore
      }
      setBgMode('classic');
    }
  }
  return { bgMode, setBgMode, toggleAnimation, applyOwnerDefault, effectiveShaderTheme };
}
